import React, { useEffect, useRef, useState } from "react";
import { chooseColonyFolder, disconnectColonyFolder, downloadColonySave, importColonySave, loadFromSelectedFolder, saveToSelectedFolder, saveNamedColonyFile, listColonyFiles, loadNamedColonyFile } from "../game/playerSaveFiles";
import { useGame } from "../game/em2c-store";
import { formatResourceAmount, formatResourceRate } from "../game/resourceUnits";
import {
  Zap, Droplet, Wind, Wheat, Users, Sun, Moon, CloudFog,
  Pause, Play, Save, FolderOpen, Download, Upload, FolderPlus,
} from "lucide-react";

const SURVIVAL = {
  power: { label: "Power", color: "#ffc107", icon: Zap },
  water: { label: "Clean Water", color: "#00e5ff", icon: Droplet },
  oxygen: { label: "Oxygen", color: "#7fffd4", icon: Wind },
  food: { label: "Food", color: "#a3e635", icon: Wheat },
};

function SurvivalCell({ id }) {
  const meta = SURVIVAL[id];
  const val = useGame((s) => s.resources[id]);
  const net = useGame((s) => s.netRates?.[id] ?? 0);
  const Icon = meta.icon;
  if (!val) return null;

  return (
    <button
      type="button"
      onClick={() => { window.dispatchEvent(new CustomEvent("mars:menu-open", { detail: { name: "resources" } })); window.dispatchEvent(new CustomEvent("mars:open-resource", { detail: { resourceKey: id } })); }}
      className="flex items-center gap-2 px-3 border-r border-white/10 min-w-[110px] hover:bg-white/[0.04] cursor-pointer text-left"
      data-testid={`resource-${id}`}
      title={`${meta.label}: ${Math.floor(val.stored)}/${Math.floor(val.max)} · ${(net >= 0 ? "+" : "")}${net.toFixed(1)}/s`}
    >
      <Icon size={15} style={{ color: meta.color }} className="shrink-0" />
      <div className="leading-tight min-w-0">
        <div className="text-[9px] font-mono uppercase tracking-wider text-slate-500 truncate">{meta.label}</div>
        <div className="flex items-baseline gap-1.5">
          <span className="font-mono text-xs text-white">{formatResourceAmount(id, val.stored)}<span className="text-slate-600"> / {formatResourceAmount(id, val.max)}</span></span>
          <span className={`font-mono text-[9px] ${net >= 0 ? "text-[#00fa9a]" : "text-[#ff6b6b]"}`}>
            {net >= 0 ? "▲ +" : "▼ "}{formatResourceRate(id, Math.abs(net))}
          </span>
        </div>
      </div>
    </button>
  );
}

export default function TopBar() {
  const fileInputRef = useRef(null);
  const [saveStatus, setSaveStatus] = useState("");
  const [saveScreenOpen, setSaveScreenOpen] = useState(false);
  const [saveSlots, setSaveSlots] = useState([]);
  const refreshSaveSlots = () => listColonyFiles().then(setSaveSlots).catch(() => setSaveSlots([]));
  useEffect(() => { const close = e => { if (e.detail?.name !== "saves") setSaveScreenOpen(false); }; window.addEventListener("mars:menu-open", close); return () => window.removeEventListener("mars:menu-open", close); }, []);
  const chooseFolder = async () => {
    try {
      save("manual");
      const result = await chooseColonyFolder();
      setSaveStatus("Mars_Colony folder connected");
      refreshSaveSlots();
      if (result.existing) {
        if (window.confirm("Existing Mars Colony save found. Load it from this folder?")) {
          await loadFromSelectedFolder();
          window.location.reload();
        } else {
          await disconnectColonyFolder(); // Never overwrite an existing save without consent.
          setSaveStatus("Folder disconnected; existing file preserved");
        }
      }
    }
    catch (e) { if (e.name !== "AbortError") setSaveStatus(e.message || "Folder access failed"); }
  };
  const importFile = async (file) => {
    if (!file) return;
    try { await importColonySave(file); window.location.reload(); }
    catch (e) { setSaveStatus(e.message || "Invalid save"); }
  };
  const sol = useGame((s) => s.sol);
  const time = useGame((s) => s.time);
  const colonists = useGame((s) => s.colonists.length);
  const isPaused = useGame((s) => s.isPaused);
  const gameSpeed = useGame((s) => s.gameSpeed);
  const storm = useGame((s) => s.storm);
  const togglePause = useGame((s) => s.togglePause);
  const setSpeed = useGame((s) => s.setSpeed);
  const save = useGame((s) => s.save);
  const load = useGame((s) => s.load);
  const toggleGrid = useGame((s) => s.toggleGrid);
  const gridVisible = useGame((s) => s.gridVisible);

  const hour = Math.floor(time * 24);
  const minute = Math.floor((time * 24 - hour) * 60);
  const isNight = time < 0.15 || time > 0.85;

  return (
    <>
    <div
      className="absolute top-0 left-0 right-0 z-30 pointer-events-auto flex items-stretch h-14 bg-[#0A0C10]/88 backdrop-blur-md border-b border-white/10"
      style={{ fontFamily: "Rajdhani, sans-serif" }}
    >
      <div className="flex items-center gap-2 px-4 border-r border-white/10 shrink-0">
        <div className="w-2 h-8" style={{ background: "linear-gradient(180deg, #D85C31, #9A321E)" }} />
        <div className="leading-tight hidden xl:block">
          <div className="text-xs tracking-[0.2em] text-slate-400 uppercase font-mono">Mars Colony</div>
          <div className="text-sm font-bold tracking-wide text-white uppercase">First Settlement</div>
        </div>
      </div>

      <div className="flex items-stretch flex-1 min-w-0 overflow-x-auto" style={{ scrollbarWidth: "none" }}>
        {Object.keys(SURVIVAL).map((id) => <SurvivalCell key={id} id={id} />)}
        <div className="flex items-center gap-2 px-3 border-r border-white/10 min-w-[100px]" data-testid="resource-colonists">
          <Users size={15} className="text-slate-200" />
          <div className="leading-tight">
            <div className="text-[9px] font-mono uppercase tracking-wider text-slate-500">Colonists</div>
            <div className="font-mono text-xs text-white">{colonists}</div>
          </div>
        </div>
      </div>

      <div className="flex items-center gap-2 px-3 border-l border-white/10 shrink-0" title={saveStatus}>
        {storm.active && (
          <div className="flex items-center gap-1 px-2 py-1 border border-[#ff3366]/60 bg-[#ff3366]/10" data-testid="storm-indicator">
            <CloudFog size={14} className="text-[#ff3366] animate-pulse" />
            <span className="text-[9px] font-mono uppercase tracking-widest text-[#ff3366]">Storm</span>
          </div>
        )}

        <div className="flex items-center gap-2 px-2">
          {isNight ? <Moon size={15} className="text-slate-300" /> : <Sun size={15} className="text-[#ffc107]" />}
          <div className="leading-tight">
            <div className="text-[9px] font-mono uppercase tracking-widest text-slate-500">Sol</div>
            <div className="font-mono text-xs text-white">{sol} · {String(hour).padStart(2, "0")}:{String(minute).padStart(2, "0")}</div>
          </div>
        </div>

        <button
          onClick={toggleGrid}
          className={`flex items-center gap-1 px-2 h-8 border text-[9px] font-mono uppercase tracking-wider ${gridVisible ? "border-cyan-400 bg-cyan-500/20 text-cyan-400" : "border-white/20 bg-white/5 text-white hover:border-cyan-400"}`}
          title="Toggle hex grid (G)"
          data-testid="btn-toggle-hex-view"
        >
          <span>⬡</span> HEX
        </button>
        <button onClick={() => { window.dispatchEvent(new CustomEvent("mars:menu-open", { detail: { name: "saves" } })); setSaveScreenOpen(true); refreshSaveSlots(); }} className="h-8 px-3 flex items-center gap-1.5 border border-cyan-400/30 bg-[#17202b] hover:border-cyan-400 text-white text-[10px] uppercase font-mono" title="Open save and load game screen"><FolderOpen size={13} /> Saves</button>
        <button data-testid="btn-pause" onClick={togglePause} className="w-8 h-8 flex items-center justify-center border border-white/20 bg-white/5 hover:border-cyan-400 text-white">
          {isPaused ? <Play size={13} /> : <Pause size={13} />}
        </button>
        {[1, 3, 8].map((n, i) => (
          <button
            key={n}
            data-testid={`btn-speed-${i + 1}`}
            onClick={() => setSpeed(n)}
            className={`w-8 h-8 flex items-center justify-center border font-mono text-[10px] ${gameSpeed === n && !isPaused ? "border-cyan-400 bg-cyan-500/20 text-cyan-400" : "border-white/20 bg-white/5 text-white hover:border-cyan-400"}`}
          >{n}x</button>
        ))}
      </div>
    </div>
    {saveScreenOpen && <div className="fixed inset-0 z-[200] pointer-events-auto flex items-center justify-center bg-black/75" onClick={() => setSaveScreenOpen(false)}><div role="dialog" aria-label="Mars Colony Save and Load" className="w-[min(540px,92vw)] max-h-[85vh] overflow-y-auto bg-[#0b111a] border border-cyan-400/50 p-5 shadow-2xl text-white" onClick={e => e.stopPropagation()}><div className="flex items-center justify-between border-b border-white/15 pb-3 mb-4"><h2 className="font-mono text-base uppercase tracking-widest text-cyan-300">Mars Colony · Save / Load</h2><button onClick={() => setSaveScreenOpen(false)} className="border border-white/30 px-3 py-1" aria-label="Close save screen">✕</button></div><p className="text-xs text-slate-300 mb-4">Choose any parent folder once. The game automatically creates <b>Mars_Colony</b> inside it and maintains Mars_Colony_Autosave.json. Downloaded backup filenames are timestamped.</p><div className="grid grid-cols-1 sm:grid-cols-2 gap-2">{[["Save Game", () => { save("manual"); saveNamedColonyFile().then(ok => { setSaveStatus(ok ? "New numbered save file created" : "Saved in browser; choose a folder for disk saves"); refreshSaveSlots(); }).catch(e => setSaveStatus(e.message)); }],["Choose Save Folder", chooseFolder],["Download Backup", () => { save("manual"); downloadColonySave(); setSaveStatus("Timestamped backup downloaded"); }],["Load From Folder", async () => { try { if(await loadFromSelectedFolder()) window.location.reload(); else setSaveStatus("No connected folder"); } catch(e){ setSaveStatus(e.message); } }],["Load Browser Save", () => { load(); setSaveScreenOpen(false); }],["Import Backup", () => fileInputRef.current?.click()]].map(([label, action]) => <button key={label} onClick={action} className="border border-white/20 bg-[#192432] hover:border-cyan-400 py-3 px-2 text-xs font-mono text-left">{label}</button>)}</div><div className="mt-4 border-t border-white/15 pt-3"><div className="flex justify-between items-center mb-2"><h3 className="font-mono text-xs text-cyan-300">Saved Games</h3><button onClick={refreshSaveSlots} className="text-xs border border-white/25 px-2 py-1">Refresh</button></div><div className="max-h-40 overflow-y-auto space-y-1">{saveSlots.length ? saveSlots.map(slot => <button key={slot.name} onClick={async () => { try { await loadNamedColonyFile(slot.name); window.location.reload(); } catch(e) { setSaveStatus(e.message); } }} className="w-full flex items-center justify-between gap-2 border border-white/15 p-2 text-left text-xs hover:border-cyan-400"><span className="truncate">{slot.name}</span><span className="text-slate-400 shrink-0">{new Date(slot.modified).toLocaleString()}</span></button>) : <p className="text-xs text-slate-500">Choose your Mars_Colony folder to see saved games.</p>}</div></div><div className="mt-4 text-xs text-amber-300 min-h-5">{saveStatus}</div><input ref={fileInputRef} type="file" accept=".json,application/json" className="hidden" onChange={e => { importFile(e.target.files?.[0]); e.target.value = ""; }} /></div></div>}
    </>
  );
}
